import React, { useEffect, useRef, useState } from "react";
import { Select } from "antd";
import FilterTabs from "./FilterTabs";
import { DATE_FILTER_OPTIONS } from "../../utils/constants";
import { getDateFilterRange } from "../../utils";

const ALL_OPTIONS = [
  DATE_FILTER_OPTIONS.MONTH_TILL_DATE,
  DATE_FILTER_OPTIONS.LAST_MONTH,
  DATE_FILTER_OPTIONS.LAST_90_DAYS,
];

// ─── Date Filter ────────────────────────────────────────────────────────────
// Preset date-range picker (Month Till Date / Last Month / Last 90 Days),
// rendered as tabs or an antd Select. Both variants share the same date math
// (see utils/getDateFilterRange) so the UI choice never affects the range.
const DateFilter = ({
  variant = "tabs",
  hideMonthTillDate = false,
  hideLastMonth = false,
  hideLast90Days = false,
  value,
  defaultValue,
  onChange,
}) => {
  const hiddenMap = {
    [DATE_FILTER_OPTIONS.MONTH_TILL_DATE]: hideMonthTillDate,
    [DATE_FILTER_OPTIONS.LAST_MONTH]: hideLastMonth,
    [DATE_FILTER_OPTIONS.LAST_90_DAYS]: hideLast90Days,
  };
  const options = ALL_OPTIONS.filter((opt) => !hiddenMap[opt]);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() =>
    defaultValue && options.includes(defaultValue) ? defaultValue : options[0]
  );

  // Fall back to the first available option if the selected one is hidden.
  const requested = isControlled ? value : internalValue;
  const selected = options.includes(requested) ? requested : options[0];

  const lastAnnounced = useRef(null);
  useEffect(() => {
    if (!selected || lastAnnounced.current === selected) return;
    lastAnnounced.current = selected;
    if (!isControlled) setInternalValue(selected);
    onChange?.(selected, getDateFilterRange(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const handleSelect = (next) => {
    lastAnnounced.current = next;
    if (!isControlled) setInternalValue(next);
    onChange?.(next, getDateFilterRange(next));
  };

  if (options.length === 0) return null;

  if (variant === "select") {
    return (
      <Select
        value={selected}
        onChange={handleSelect}
        showSearch={false}
        className="min-w-[170px]"
        options={options.map((opt) => ({ value: opt, label: opt }))}
      />
    );
  }

  return <FilterTabs tabs={options} active={selected} onChange={handleSelect} />;
};

export default DateFilter;
